import LocalForage from 'localforage';
import Vue from 'vue';
import Vuex, { ModuleTree, Store } from 'vuex';

import { Context } from '@/@types/ssr';

import { CONTEXT_CLASSES, type SnowplowContextName } from '@/helpers/tracking/providers';
import { PersistStateCookiesPlugin, PersistStateLocalStoragePlugin } from '@/persist-state.plugin';

import { CONSTANT_INIT_FREEZE_ITEMS } from '@/stores/constant.store';
import { LANGUAGE_INIT_FREEZE_ITEMS } from '@/stores/language.store';
import Cookies from 'js-cookie';

Vue.use(Vuex);

/**
 * Require All Store modules
 */

function camelCase(str: string): string {
	return str.replace(/-([a-z])/g, function (g) {
		return g[1].toUpperCase();
	});
}
export let store: Store<any>;
export const useStore = () => store;
export type FreezingItems<T> = Record<string, (state: T) => void>;

// when objects are frozen before they are put into vuex or vue component data,
// it will skip the defineReactive step that will create dependencies and subscribers that keep track of dependencies and notifies them upon change.
// we don't need this, if we don't expect properties to change.
// e.g. properties like full_path, name, offers of a single title will not change. we only need to keep track of the different items.
// so we keep the items reactive, but the single item can be frozen.
//
// defineReactive: (https://github.com/vuejs/vue/blob/0603ff695d2f41286239298210113cbe2b209e28/src/core/observer/index.js#L135)
const freezeItemsByStore: Record<string, FreezingItems<any>> = {
	constant: CONSTANT_INIT_FREEZE_ITEMS,
	language: LANGUAGE_INIT_FREEZE_ITEMS,
};

// used for LocalForage's config, so stores are contained in a namespace à la jw/user.
export const STORE_NAMESPACE = 'jw';

export default async function (context: Partial<Context>): Promise<any> {
	const { ssrData, req } = context;
	const isSeoUser = ssrData?.isSeoUser ?? true;
	const requestCookies = req?.cookies;

	const requireStore = require.context('./stores', false, /\.store\.ts$/);
	const stores: ModuleTree<any> = {};
	let savedState: any = {};
	let baseState: any = {}; // either taken from server (__DATA__.state) or initial state from the stores
	let cookieHasAuth = false; // track if cookies indicate authenticated state
	let didSyncLogoutFromCookies = false; // track if we explicitly synced logout state from cookies
	let didSyncLoginFromCookies = false; // track if we explicitly synced login state from cookies

	requireStore.keys().forEach(fileName => {
		const moduleName = camelCase(fileName.replace(/(\.\/|\.store\.ts)/g, ''));
		stores[moduleName] = requireStore(fileName).default;
	});

	// Storage migration
	// check if anything needs to be migrated from the old localStorage
	if (process.client) {
		const { needsLocalStorageMigration } = await import('@/migrate-local-storage');
		// from old structure (localStorage) to new structure (localStorage)
		if (await needsLocalStorageMigration()) {
			const { migrateLocalStorage } = await import('@/migrate-local-storage');
			await migrateLocalStorage();
		}

		// cookie migration, just delete the old ones, persist-cookie plugin will save the new ones correctly
		// don't do it yet
		// if (Cookies.get('jw/user')) {
		// 	Cookies.remove('jw/user');
		// }

		// if (Cookies.get('jw/filter')) {
		// 	Cookies.remove('jw/filter');
		// }

		// client: set savedState from localStorage
		savedState = await LocalForage.getItems();

		// Always check cookies for cross-domain auth sync (not just on subdomains)
		// Cookies are the source of truth for auth state across all domains
		const userCookie = Cookies.get(`${STORE_NAMESPACE}_user`) || Cookies.get(`${STORE_NAMESPACE}/user`);
		const accessTokenCookie = Cookies.get('access_token');

		// Parse cookie to check for actual auth state
		let parsedCookieUser: any = null;
		let cookieUserJwLoginId: string | undefined;
		if (userCookie) {
			try {
				parsedCookieUser = JSON.parse(userCookie);
				cookieUserJwLoginId = parsedCookieUser?.jwLoginId;
			} catch (err) {
				const { captureMessageForSentry } = await import('@/helpers/sentry-helper');

				captureMessageForSentry(
					'[store.ts] Error parsing user cookie',
					{ err, where: 'store.ts createStore (cookie parsing)' },
					'error'
				);
			}
		}

		// Detect if cookies and localStorage are out of sync
		// Cookie has auth if: user cookie exists + has jwLoginId + accessToken cookie exists
		cookieHasAuth = !!(userCookie && cookieUserJwLoginId && accessTokenCookie);
		const localStorageHasAuth = !!(savedState.user?.jwLoginId && savedState.user?.accessToken);

		// Sync from cookies if they're out of sync
		if (cookieHasAuth !== localStorageHasAuth) {
			if (cookieHasAuth) {
				// Cookie says logged in, localStorage says logged out → Sync login state

				// Start with cookie data as base (has all necessary fields including jwId)
				// Then merge in localStorage preferences to preserve user customizations
				// Note: Only include properties that are actually persisted to cookies (see persist-state.plugin.ts)
				const authFieldsFromCookie = {
					jwId: parsedCookieUser.jwId,
					jwLoginId: parsedCookieUser.jwLoginId,
					accessToken: accessTokenCookie,
					loggedInProviders: parsedCookieUser.loggedInProviders ?? [],
					isPremium: parsedCookieUser.isPremium,
					userRole: parsedCookieUser.userRole,
					onboardingStatus: parsedCookieUser.onboardingStatus,
					lastUsedLoginProvider: parsedCookieUser.lastUsedLoginProvider,
					accountCreationTime: parsedCookieUser.accountCreationTime,
				};

				// Merge: cookie as base, then layer localStorage on top, then auth fields override
				savedState.user = {
					...parsedCookieUser, // Base: all fields from cookie
					...savedState.user, // Override: preferences from localStorage
					...authFieldsFromCookie, // Final override: auth fields from cookie
				};

				// Update LocalForage to ensure persistence layer has correct data
				await LocalForage.setItem('user', savedState.user);
				localStorage.setItem('jw/user', JSON.stringify(savedState.user));

				// Track that we explicitly synced login state from cookies
				// This is used later to prevent server baseState from causing hydration mismatches
				didSyncLoginFromCookies = true;
			} else {
				// Cookie says logged out, localStorage says logged in → Sync logout state

				// Preserve existing user object, only clear auth-related fields
				if (savedState.user) {
					savedState.user.jwLoginId = undefined;
					savedState.user.accessToken = undefined;
					savedState.user.loggedInProviders = [];
					savedState.user.isPremium = false;
					savedState.user.userRole = 'USER';
					savedState.user.info = {};
					savedState.user.profileInfo = null;
					savedState.user.onboardingStatus = 'GLOBAL';
					savedState.user.lastUsedLoginProvider = parsedCookieUser?.lastUsedLoginProvider || '';
					savedState.user.accountCreationTime = null;
					savedState.user.profileIsComplete = false;
				}

				await LocalForage.setItem('user', savedState.user);
				localStorage.setItem('jw/user', JSON.stringify(savedState.user));

				// Track that we explicitly synced logout state from cookies
				// This is used later to determine if we should ignore SSR baseState
				didSyncLogoutFromCookies = true;
			}
		}

		baseState = (window as any).__DATA__ ? (window as any).__DATA__.state : {};
	} else if (!isSeoUser && requestCookies) {
		// server: set savedState from cookies
		const _userCookie = requestCookies[`${STORE_NAMESPACE}_user`] || requestCookies[`${STORE_NAMESPACE}/user`];

		if (_userCookie) {
			try {
				savedState.user = JSON.parse(_userCookie);

				// ACCESS_TOKEN REMOVAL FROM JW_USER COOKIE LOGS.
				if (!!savedState.user?.accessToken) {
					if (!requestCookies['access_token']) {
						console.error(
							`[Store.ts] createStore : access_token cookie missing while user.accessToken is set`
						);
					}
				}
				const isLoggedIn = (savedState.user?.loggedInProviders?.length ?? 0) > 0;
				if (isLoggedIn && !requestCookies['access_token']) {
					if (!savedState.user?.accessToken) {
						console.error(
							`[Store.ts] createStore : accessToken completely missing while user is logged in`
						);
					} else {
						console.error(`[Store.ts] createStore : access_token cookie missing while user is logged in`);
					}
				}

				// jw/user cookie removal logs.
				if (!requestCookies[`${STORE_NAMESPACE}_user`] && !!requestCookies[`${STORE_NAMESPACE}/user`]) {
					console.error(
						`[Store.ts] createStore : ${STORE_NAMESPACE}/user cookie found instead of ${STORE_NAMESPACE}_user`
					);
				}

				// Force access_token cookie handover
				if (requestCookies['access_token']) {
					savedState.user.accessToken = requestCookies['access_token'];
				}
			} catch (err) {
				console.error(`[Store.ts] createStore error parsing ${STORE_NAMESPACE}_user`, _userCookie);
			}
		}

		const _filterCookie =
			requestCookies[`${STORE_NAMESPACE}_filter`] || requestCookies[`${STORE_NAMESPACE}/filter`];
		if (_filterCookie) {
			try {
				savedState.filter = JSON.parse(_filterCookie);
			} catch (err) {
				console.error(`[Store.ts] createStore error parsing ${STORE_NAMESPACE}_filter`, _filterCookie);
			}
		}
	}

	// merging initialState and saved state
	const modules = Object.keys(stores).reduce((root, storeKey) => {
		// if there is server state, use that as the default/base state, otherwise default store state
		// EXCEPTION: For user store, if we just synced auth state from cookies, check if baseState is stale
		// Only ignore baseState if server auth state doesn't match cookie auth state (hydration mismatch)
		// If server auth matches cookies, baseState is fresh and should be used (has latest settings)
		let shouldIgnoreBaseState = false;
		if (storeKey === 'user' && (didSyncLogoutFromCookies || didSyncLoginFromCookies)) {
			// Check if server baseState auth matches cookie auth
			const baseStateHasAuth = !!(baseState.user?.jwLoginId && baseState.user?.accessToken);
			const authStatesMatch = cookieHasAuth === baseStateHasAuth;

			// Only ignore baseState if auth states don't match (server stale)
			// If they match, baseState is fresh and we should use it for settings
			shouldIgnoreBaseState = !authStatesMatch;
		}

		const state = !shouldIgnoreBaseState && baseState[storeKey] ? baseState[storeKey] : stores[storeKey].state();

		if (process.client) {
			if (storeKey === 'user') {
				// special treatment user: it's possible that localStorage.user.settings is outdated
				// and the server has the latest user settings anyway, so we always take that for now
				// @todo: this will have to go when we have a more solid solution about state juggling between client <-> server
				// the server always fetches the user's latest settings
				const isBaseDataEmpty = Object.keys(baseState.user || {}).length === 0;

				savedState.user = {
					...savedState.user,
					...(isBaseDataEmpty
						? {}
						: {
								settings: baseState.user?.settings,
								preferences: baseState.user?.preferences,
								newBuyboxFilterType: baseState.user?.newBuyboxFilterType,
								newBuyboxView: baseState.user?.newBuyboxView,
								buyboxExpView: baseState.user?.buyboxExpView,
						  }),
				};

				// SAVE INITIAL USER STATE;
				if (window) {
					(window as any).__INITIAL_SAVED_USER_STATE__ = JSON.parse(JSON.stringify(savedState.user));
				}
			} else if (storeKey === 'filter' && baseState.filter) {
				savedState.filter = baseState.filter;
			} else if (
				storeKey === 'tracking' &&
				state.queues &&
				Object.keys(state.queues).some(key => (state.queues[key] || []).length > 0)
			) {
				// special treatment tracking: that might need to instantiate SnowplowContext classes
				// unserialize tracking contexts to SnowplowContext objects
				state.queues = Object.keys(state.queues).reduce((root, key) => {
					root[key] = (state.queues[key] || []).map((item: any) => {
						return {
							...item,
							contexts: item.contexts.map((serializedContext: { __name: SnowplowContextName }) => {
								// unserialize the stringified context, that comes from the server in __DATA__
								return Object.create(
									CONTEXT_CLASSES[serializedContext.__name].prototype,
									Object.getOwnPropertyDescriptors(serializedContext)
								);
							}),
						};
					});
					return root;
				}, state.queues);
			}
		}

		// other stores being merged with savedState
		const stateItem = {
			...state,
			...(savedState[storeKey] || {}),
		};

		if (freezeItemsByStore[storeKey]) {
			Object.keys(freezeItemsByStore[storeKey])
				// we throw the final merged state into each defined case that needs specific items to be frozen
				.forEach(id => freezeItemsByStore[storeKey][id](stateItem));
		}

		root[storeKey] = {
			...stores[storeKey],
			state: (() => stateItem)(),
		};

		// #VUEX-GETTER-DEBUG
		// if (process.server) {
		// 	const { performance } = require('perf_hooks');

		// 	Object.keys(root[storeKey].getters).forEach(getterName => {
		// 		const fn = root[storeKey].getters[getterName];
		// 		root[storeKey].getters[getterName] = function() {
		// 			if (!accesses[`${storeKey}/${getterName}`]) {
		// 				accesses[`${storeKey}/${getterName}`] = {
		// 					calls: 0,
		// 					totalTime: 0,
		// 				};
		// 			}
		// 			const startTime = performance.now();
		// 			const ret = fn.apply(this, arguments);
		// 			const endTime = performance.now();
		// 			accesses[`${storeKey}/${getterName}`].totalTime += endTime - startTime;
		// 			accesses[`${storeKey}/${getterName}`].calls++;
		// 			return ret;
		// 		};
		// 	});
		// }

		return root;
	}, {} as any);

	let plugins: Array<any> = [];
	if (process.client) {
		const stores = ['user', 'filter'];
		plugins = [PersistStateLocalStoragePlugin(stores)];

		plugins.push(PersistStateCookiesPlugin());
	}
	store = new Vuex.Store<any>({
		modules,
		strict: process.env.NODE_ENV === 'development',
		plugins,
	});

	return store;
}
